import { useMemo } from 'react';
import {
  ATTRIBUTE_GROUP_LABELS,
  GROUP_SCOPE,
  attributesAvailableUpTo,
  refKey,
  type AttributeDef,
  type AttributeGroup,
  type AttributeScope,
} from '../../../Campaigns/guided/config/attributes';
import {
  useCampaignCapabilities,
  useCampaignOutcomes,
  useCampaignQuestions,
  useCustomerPersonas,
  useWorkshopTeamRoles,
} from '../../../../hooks/campaign-hooks';
import { byOrder } from '../../../../support/campaigns';

/**
 * The live insert-token options for the admin picker: the pure attribute catalog (`config/attributes`)
 * expanded against a campaign's live reference rows into concrete `{{group.key}}` tokens with resolved
 * labels, grouped for a menu and filtered by point-in-campaign availability.
 *
 * DB-driven only, honoring the no-fallback directive: the reference families (outcomes / personas /
 * roles / capabilities / questions) are expanded from the campaign's own hook rows, so an unauthored
 * group simply produces no options and is dropped. Exact static/dynamic tokens (customer basics, open
 * gaps, deep-dive findings, the schedule roster) are always referenceable and always listed. Salesforce
 * field families and the captured-answer family have no DB enumeration source, so they are not expanded
 * here — a hand-typed `{{sf.account.industry}}` / `{{answer.step.q}}` still resolves at runtime.
 *
 * The concrete keys are minted with the SAME `refKey(label, id)` the resolver's context builder uses
 * (`api/campaignData.ts` `resolveAttributeReferences`), so a token the picker inserts matches the key
 * the resolver looks up. Capabilities carry a native `key`, so they are addressed by it directly.
 */

export interface InsertTokenOption {
  /** The canonical `{{group.key}}` token this option inserts. */
  token: string;
  /** Human label shown in the menu. */
  label: string;
  help?: string;
  /** The equivalent legacy `[Bracketed]` spelling, shown as an author hint. */
  legacyAlias?: string;
}

export interface InsertTokenGroup {
  group: AttributeGroup;
  groupLabel: string;
  options: InsertTokenOption[];
}

/** Catalog group display order for the grouped menu. */
const GROUP_ORDER: AttributeGroup[] = [
  'customer',
  'salesforce',
  'outcome',
  'persona',
  'role',
  'capability',
  'question',
  'answer',
  'gap',
  'deepdive',
  'webex',
];

const truncate = (s: string, n = 60): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function useAttributeOptions(
  campaignId: number | undefined,
  opts?: { uptoStepOrder?: number; scope?: AttributeScope },
): InsertTokenGroup[] {
  const { data: outcomeRows } = useCampaignOutcomes(campaignId);
  const { data: questionRows } = useCampaignQuestions(campaignId);
  const { data: capabilityRows } = useCampaignCapabilities(campaignId);
  const { personas } = useCustomerPersonas(campaignId);
  const { roles } = useWorkshopTeamRoles(campaignId);
  const uptoStepOrder = opts?.uptoStepOrder;
  // Authoring scope: an enablement field ('campaign') hides delivery-scoped groups (customer /
  // Salesforce / captured answers / gaps / deep-dive) that can never resolve there; a per-delivery
  // field ('delivery', the permissive default) offers every group. Purely a picker affordance —
  // the runtime resolver is scope-agnostic and leaves any unresolved token verbatim.
  const scope = opts?.scope;

  return useMemo(() => {
    const available = attributesAvailableUpTo(uptoStepOrder);

    const outcomes = byOrder((outcomeRows ?? []).filter((o) => !o.hidden));
    const questions = byOrder((questionRows ?? []).filter((q) => !q.hidden));
    const capabilities = byOrder((capabilityRows ?? []).filter((c) => !c.hidden));
    const personaRows = byOrder(personas ?? []);
    const roleRows = byOrder(roles ?? []);

    // Expand a family def into concrete per-instance options from the matching live rows.
    const familyOptions = (def: AttributeDef): InsertTokenOption[] => {
      switch (def.group) {
        case 'outcome':
          return outcomes.map((o) => ({
            token: `{{outcome.${refKey(o.title, o.id)}.title}}`,
            label: truncate(o.title),
            help: def.help,
          }));
        case 'persona':
          return personaRows.map((p) => ({
            token: `{{persona.${refKey(p.label, p.id)}.label}}`,
            label: truncate(p.label),
            help: def.help,
          }));
        case 'role':
          return roleRows.map((r) => ({
            token: `{{role.${refKey(r.name, r.id)}.name}}`,
            label: truncate(r.name),
            help: def.help,
          }));
        case 'capability':
          return capabilities.map((c) => ({
            token: `{{capability.${c.key}.name}}`,
            label: truncate(c.name),
            help: def.help,
          }));
        case 'question':
          return questions.map((q) => ({
            token: `{{question.${refKey(q.text, q.id)}.text}}`,
            label: truncate(q.text),
            help: def.help,
          }));
        default:
          // salesforce.*, answer.* — no DB enumeration source; resolve only if hand-typed.
          return [];
      }
    };

    const buckets = new Map<AttributeGroup, InsertTokenOption[]>();
    const push = (group: AttributeGroup, option: InsertTokenOption) => {
      const list = buckets.get(group) ?? [];
      list.push(option);
      buckets.set(group, list);
    };

    for (const def of available) {
      // In an enablement field, drop delivery-scoped groups entirely (they can't resolve there).
      if (scope === 'campaign' && GROUP_SCOPE[def.group] === 'delivery') continue;
      if (def.pattern) {
        familyOptions(def).forEach((opt) => push(def.group, opt));
      } else {
        push(def.group, {
          token: `{{${def.key}}}`,
          label: def.label,
          help: def.help,
          legacyAlias: def.legacyAlias,
        });
      }
    }

    // Catalog group order; drop empty groups (DB-driven ⇒ absent).
    return GROUP_ORDER.filter((g) => (buckets.get(g)?.length ?? 0) > 0).map((g) => ({
      group: g,
      groupLabel: ATTRIBUTE_GROUP_LABELS[g],
      options: buckets.get(g)!,
    }));
  }, [outcomeRows, questionRows, capabilityRows, personas, roles, uptoStepOrder, scope]);
}
